import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Shield, Wallet, ChevronRight, Lock, AlertTriangle } from 'lucide-react';

const ADMIN_WALLET = 'C2kQkiowaVKZDXeFd7LE84WZJowrpgYA7DrhPpxqY6S4';

export default function AdminAccess() {
  const navigate = useNavigate();
  const { connected, publicKey } = useWallet();
  const { setVisible } = useWalletModal();
  const [error, setError] = useState('');

  const walletAddress = publicKey?.toBase58() || '';
  const isAdmin = walletAddress === ADMIN_WALLET;

  useEffect(() => {
    if (connected && isAdmin) {
      sessionStorage.setItem('gfg_admin_access', 'true');
    }
  }, [connected, isAdmin]);

  return (
    <div className="min-h-screen flex items-center justify-center relative" style={{ background: '#06040a' }}>
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(57,255,20,0.04), transparent 70%)' }} />
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md mx-4">

        <div className="relative overflow-hidden p-8"
          style={{ background: 'rgba(10,8,16,0.9)', border: '1px solid rgba(57,255,20,0.15)', backdropFilter: 'blur(10px)' }}>
          {/* Corner brackets */}
          <div className="absolute top-0 left-0 w-3 h-3 border-t border-l" style={{ borderColor: 'rgba(57,255,20,0.25)' }} />
          <div className="absolute top-0 right-0 w-3 h-3 border-t border-r" style={{ borderColor: 'rgba(57,255,20,0.25)' }} />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l" style={{ borderColor: 'rgba(57,255,20,0.25)' }} />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r" style={{ borderColor: 'rgba(57,255,20,0.25)' }} />

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <motion.div className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(57,255,20,0.04)', border: '1.5px solid rgba(57,255,20,0.15)' }}
              animate={{ boxShadow: ['0 0 15px rgba(57,255,20,0.05)', '0 0 25px rgba(57,255,20,0.12)', '0 0 15px rgba(57,255,20,0.05)'] }}
              transition={{ duration: 3, repeat: Infinity }}>
              <Shield className="w-7 h-7" style={{ color: 'rgba(57,255,20,0.5)' }} />
            </motion.div>
          </div>

          <h1 className="text-2xl font-bold text-center mb-1 tracking-wider"
            style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif", color: 'rgba(57,255,20,0.7)', filter: 'drop-shadow(0 0 10px rgba(57,255,20,0.2))' }}>
            Admin Access
          </h1>
          <p className="text-center text-[10px] mb-8" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(57,255,20,0.25)' }}>
            Restricted to authorized wallets only
          </p>

          {!connected ? (
            <div className="space-y-4">
              <button onClick={() => setVisible(true)}
                className="w-full py-3.5 flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.25)', color: 'rgba(57,255,20,0.7)' }}
                data-testid="admin-connect-wallet">
                <Wallet className="w-4 h-4" />
                <span className="text-sm font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>Connect Admin Wallet</span>
              </button>
              <p className="text-center text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(57,255,20,0.15)' }}>
                Only the admin wallet can access the control panel
              </p>
            </div>
          ) : isAdmin ? (
            <div className="space-y-4">
              <div className="p-3 flex items-center gap-2" style={{ background: 'rgba(57,255,20,0.05)', border: '1px solid rgba(57,255,20,0.2)' }}>
                <motion.div className="w-2 h-2 rounded-full" style={{ background: '#39ff14', boxShadow: '0 0 8px rgba(57,255,20,0.6)' }}
                  animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }} />
                <span className="text-[10px] font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(57,255,20,0.5)' }}>
                  {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
                </span>
                <span className="ml-auto text-[9px] px-2 py-0.5 font-bold" style={{ background: 'rgba(57,255,20,0.1)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.3)' }}>
                  ADMIN
                </span>
              </div>
              <button onClick={() => navigate('/admin')}
                className="w-full py-3.5 flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'rgba(57,255,20,0.08)', border: '1px solid rgba(57,255,20,0.3)', color: '#39ff14', boxShadow: '0 0 15px rgba(57,255,20,0.06)' }}
                data-testid="admin-enter-panel">
                <Lock className="w-4 h-4" />
                <span className="text-sm font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>Enter Control Panel</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 flex items-center gap-2 text-center" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(239,68,68,0.5)' }} />
                <span className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(239,68,68,0.5)' }}>
                  Wallet not authorized. Access denied.
                </span>
              </div>
              <button onClick={() => navigate('/')}
                className="w-full py-3 text-xs text-center transition-all"
                style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                Return to Sewers
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
